/* File: src\components\settings\SettingsPage.js */

/* src/components/settings/SettingsPage.js */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
// --- Import Context hooks ---
// NOTE: Assuming getDefaultSettings is exported from SettingsContext
import { useSettings, getDefaultSettings } from '../../contexts/SettingsContext';
import { useProfile } from '../../contexts/ProfileContext';
// --- Import UI Components ---
import ConfirmationModal from '../ui/ConfirmationModal';
// --- MODIFICATION START: Remove AudioTest import ---
// import AudioTest from '../AudioTest'; // No longer needed
// --- MODIFICATION END ---
// --- Import Routing & Icons ---
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faVolumeHigh,
    faSpinner,
    faKeyboard,
    faCog,
    faClock,
    faBrain,
    faSlidersH, // Added icon for page title
    faFlask // Icon for Experimental features
} from '@fortawesome/free-solid-svg-icons';

// Import timezone options
import { TIMEZONE_OPTIONS } from '../../constants/timezoneData';

// --- MODIFICATION START: Import audioManager ---
import audioManager from '../../utils/audioManager';
// --- MODIFICATION END ---

// --- Reusable UI Components ---

// Loading Spinner
const LoadingSpinner = () => <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>;

// Section Card (Updated to include icon support, matching AccountPage style)
const SectionCard = ({ title, icon, children }) => (
    <div className="bg-white p-4 sm:p-6 border border-gray-200 rounded-lg shadow-sm mb-6 transition-shadow hover:shadow-md">
        <div className="flex items-center mb-4 pb-2 border-b border-gray-200">
            {icon && <FontAwesomeIcon icon={icon} className="mr-3 h-5 w-5 text-indigo-600" />}
            <h3 className="text-lg font-semibold text-gray-800">{title}</h3> {/* Changed text color, removed mb */}
        </div>
        {children}
    </div>
);

// Key Binding Input (Updated with disabled prop)
const KeyBindingInput = ({ label, currentKey, isSetting, onStartSetting, disabled = false }) => {
    const formatKeyDisplay = (key) => {
        if (!key) return 'None'; if (key === ' ') return 'Spacebar';
        if (key === 'ArrowLeft') return '←'; if (key === 'ArrowRight') return '→';
        if (key === 'ArrowUp') return '↑'; if (key === 'ArrowDown') return '↓';
        if (key.length === 1) return key.toUpperCase();
        if (key.startsWith('Key')) return key.substring(3).toUpperCase();
        if (key.startsWith('Digit')) return key.substring(5);
        return key;
    };
    return (
        <div className="flex items-center justify-between py-2 border-b border-gray-200 last:border-b-0">
            <span className="text-sm font-medium text-gray-700">{label}:</span>
            <button
                onClick={onStartSetting}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors min-w-[120px] text-center border ${
                    isSetting
                        ? 'bg-yellow-200 text-yellow-800 border-yellow-400 animate-pulse'
                        : 'bg-gray-100 text-gray-800 border-gray-300 hover:bg-gray-200'
                    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`} // Apply disabled styles
                aria-live="polite"
                aria-label={`${label}, Current key: ${formatKeyDisplay(currentKey)}, Click to change`}
                disabled={disabled} // Disable button if needed
            >
                 {isSetting ? 'Press a key...' : formatKeyDisplay(currentKey)}
            </button>
        </div>
    );
};

// Toggle Switch Component (Updated with disabled interaction)
const ToggleSwitch = ({ label, description, checked, onChange, disabled = false }) => (
    <div className="flex items-center justify-between py-3">
        <span className="flex-grow flex flex-col mr-4">
            <span className="text-sm font-medium text-gray-700">{label}</span>
            {description && <span className="text-xs text-gray-500 mt-0.5">{description}</span>}
        </span>
        <button type="button" onClick={() => !disabled && onChange(!checked)} className={`${ checked ? 'bg-indigo-600' : 'bg-gray-200' } relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`} role="switch" aria-checked={checked} disabled={disabled} aria-label={label}>
            <span aria-hidden="true" className={`${ checked ? 'translate-x-5' : 'translate-x-0' } pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200`} />
        </button>
    </div>
);

// Dropdown Input Component
const DropdownInput = ({ label, id, value, onChange, options, disabled = false, description = null, className = "" }) => (
    <div className={`py-2 ${className}`}>
        <div className="flex items-center justify-between">
            <label htmlFor={id} className="text-sm font-medium text-gray-700 mr-4 flex-shrink-0">{label}:</label>
            <div className="relative flex-grow">
                <select
                    id={id}
                    name={id}
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                    className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md disabled:bg-gray-100 disabled:text-gray-500 appearance-none bg-white shadow-sm"
                >
                    {options.map(option => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                </div>
            </div>
        </div>
        {description && !disabled && <p className="text-xs text-gray-500 mt-1 text-right">{description}</p>}
    </div>
);

// Slider Input Component
const SliderInput = ({ label, id, value, onChange, min, max, step, disabled = false, displayValue }) => (
    <div className="py-3">
        <div className="flex items-center justify-between mb-1">
             <label htmlFor={id} className="text-sm font-medium text-gray-700">{label}:</label>
             <span className={`text-sm font-semibold ${disabled ? 'text-gray-400' : 'text-indigo-600'} tabular-nums`}>{displayValue}</span>
        </div>
        <input
            type="range"
            id={id}
            name={id}
            value={value}
            onChange={onChange}
            min={min}
            max={max}
            step={step}
            disabled={disabled}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500 accent-indigo-600"
        />
    </div>
);

/**
 * SettingsPage Component
 * Allows users to configure application settings like key bindings, instant feedback,
 * audio voice selection, timezone, and manually adjust their N-Back training level.
 */
const SettingsPage = () => {
    // --- Contexts ---
    const { settings, updateSetting, isLoading: isLoadingAppSettings, error: appSettingsError, availableVoices } = useSettings();
    const { activeProfile, updateProfileProgress, isLoading: isLoadingProfile, error: profileError } = useProfile();
    const navigate = useNavigate();

    // --- Local State for UI ---
    const [settingKey, setSettingKey] = useState(null); // Tracks which key is currently being set
    const [localKeyBindings, setLocalKeyBindings] = useState({ visual: 'ArrowLeft', audio: 'ArrowRight' });
    const [localInstantFeedback, setLocalInstantFeedback] = useState(true);
    const [localSelectedVoiceId, setLocalSelectedVoiceId] = useState(''); // For audio voice dropdown
    const [localEnableExperimentalFeatures, setLocalEnableExperimentalFeatures] = useState(false); // New setting
    const [localTimezone, setLocalTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone); // Initialize with browser timezone
    const [isSavingAppSettings, setIsSavingAppSettings] = useState(false);
    const [appSaveStatus, setAppSaveStatus] = useState({ message: '', type: null, section: null }); // section identifies which card's status to show
    // --- MODIFICATION START: Remove modal state, add audio state ---
    // const [showAudioTest, setShowAudioTest] = useState(false); // Removed
    const [audioReady, setAudioReady] = useState(false);
    const [activeButton, setActiveButton] = useState(null); // For visual feedback on letter buttons
    // --- MODIFICATION END ---

    // Profile Level Settings
    const [localNBack, setLocalNBack] = useState('2'); // String value from N-Back dropdown
    const [localDifficultySliderValue, setLocalDifficultySliderValue] = useState(0); // Integer value 0-99 from slider
    const [isSavingLevel, setIsSavingLevel] = useState(false);
    const [levelSaveStatus, setLevelSaveStatus] = useState({ message: '', type: null });
    const [showLevelConfirmModal, setShowLevelConfirmModal] = useState(false);
    const [levelValidationError, setLevelValidationError] = useState('');

    // --- Effects ---

    // Initialize local App settings state from context or defaults
    useEffect(() => {
        const defaultSettings = getDefaultSettings();
        if (!isLoadingAppSettings && settings) {
            // Key Bindings
            const bindings = (settings.keyBindings && typeof settings.keyBindings.visual === 'string' && typeof settings.keyBindings.audio === 'string')
                             ? settings.keyBindings : defaultSettings.keyBindings;
            setLocalKeyBindings(bindings);

            // Instant Feedback
            const feedback = typeof settings.instantFeedback === 'boolean'
                             ? settings.instantFeedback : defaultSettings.instantFeedback;
            setLocalInstantFeedback(feedback);

            // Audio Voice Selection
            const currentVoice = settings.selectedVoiceId;
            const firstAvailableVoice = Object.keys(availableVoices || {})[0];
            const effectiveVoiceId = (currentVoice && availableVoices && availableVoices[currentVoice])
                                    ? currentVoice
                                    : (firstAvailableVoice || ''); // Fallback to first available or empty string
            setLocalSelectedVoiceId(effectiveVoiceId); // Use the determined ID

            // Timezone
            const timezone = settings.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
            setLocalTimezone(timezone);

            // Enable Experimental Features
            const experimental = typeof settings.enableExperimentalFeatures === 'boolean'
                                ? settings.enableExperimentalFeatures : defaultSettings.enableExperimentalFeatures;
            setLocalEnableExperimentalFeatures(experimental);

        } else if (!isLoadingAppSettings && !settings) {
             // Fallback to defaults if settings context is unavailable
             setLocalKeyBindings(defaultSettings.keyBindings);
             setLocalInstantFeedback(defaultSettings.instantFeedback);
             const firstAvailableVoice = Object.keys(availableVoices || {})[0]; // Need this here too
             setLocalSelectedVoiceId(firstAvailableVoice || ''); // Use the determined ID
             setLocalTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
             setLocalEnableExperimentalFeatures(defaultSettings.enableExperimentalFeatures);
        }
    }, [isLoadingAppSettings, settings, availableVoices]); // Add availableVoices dependency

    // Initialize local Level settings state from active profile
    useEffect(() => {
        if (activeProfile) {
            // --- MODIFICATION START: Initialize level based on currentNBack, clamped to 2-20 ---
            const currentN = activeProfile.currentNBack ?? 2;
            const initialNBackClamped = Math.max(2, Math.min(20, currentN));
            setLocalNBack(String(initialNBackClamped));
            // --- MODIFICATION END ---
            const initialDifficulty = activeProfile.currentDifficulty ?? 0.0;
            // Ensure difficulty is within 0-0.99 range before scaling to slider value 0-99
            const clampedDifficulty = Math.max(0, Math.min(0.99, initialDifficulty));
            const sliderValue = Math.round(clampedDifficulty * 100);
            setLocalDifficultySliderValue(sliderValue);
            setLevelValidationError('');
        } else {
            // No active profile, default to 2
            setLocalNBack('2');
            setLocalDifficultySliderValue(0);
            setLevelValidationError('');
        }
    }, [activeProfile]);

    // Effect for capturing the key press
    useEffect(() => {
        if (!settingKey) return;
        const handleKeyDown = (e) => {
            e.preventDefault();
            const newKey = e.key;
             if (newKey === 'Escape' || newKey === 'Tab') {
                 setSettingKey(null);
                 return;
             }
            setLocalKeyBindings(prev => ({ ...prev, [settingKey]: newKey }));
            setSettingKey(null);
            // Do not auto-save here, user must click the save button for keybindings
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [settingKey]);

    // --- MODIFICATION START: Effect to initialize AudioManager ---
    useEffect(() => {
        const ready = audioManager.init();
        setAudioReady(ready);
        if (!ready) {
            console.error("SettingsPage: AudioManager initialization failed.");
        }
    }, []); // Run only on mount
    // --- MODIFICATION END ---

    // --- Handlers ---

    // Activate key setting mode
    const handleStartSettingKey = useCallback((keyType) => {
        if (isSavingAppSettings || isSavingLevel) return; // Don't allow changing while saving
        if (settingKey === keyType) {
            setSettingKey(null); // Toggle off if clicking the same button
            return;
        }
        setSettingKey(keyType);
        setAppSaveStatus({ message: '', type: null }); // Clear previous status when starting to set
    }, [settingKey, isSavingAppSettings, isSavingLevel]);

    // --- MODIFICATION START: Remove toggleAudioTest, add audio handlers ---
    // Toggle the audio test modal visibility
    // const toggleAudioTest = useCallback(() => { // Removed
    //     setShowAudioTest(prev => !prev);
    // }, []);

    const handleResumeAudio = useCallback(async () => {
        console.log("SettingsPage: Attempting to resume audio context...");
        const resumed = await audioManager.resume();
        if (resumed) {
            console.log("SettingsPage: Audio context resumed successfully.");
            setAudioReady(true);
            // Optionally play a confirmation sound if desired, but not requested
            // audioManager.playFeedbackSound('correct');
        } else {
            console.error("SettingsPage: Failed to resume audio context.");
        }
    }, []); // No dependencies needed

    // Determine if UI should be disabled generally (e.g., saving, setting key)
    const isAnySaving = isSavingAppSettings || isSavingLevel;
    const isKeySettingInProgress = !!settingKey;
    const isGeneralSectionDisabled = isAnySaving || isKeySettingInProgress;

    const handleTestLetter = useCallback(async (letter) => {
        // Prevent testing if general section is disabled
        if (isGeneralSectionDisabled) return;

        setActiveButton(`letter-${letter}`);
        const resumed = await audioManager.resume(); // Attempt resume on interaction
        setAudioReady(audioManager.getAudioContext()?.state === 'running'); // Update ready state after attempt

        if (resumed) {
            console.log(`SettingsPage: Playing letter ${letter} using voice: ${localSelectedVoiceId || 'default'}...`);
            audioManager.playItem(letter, 0.5, { voiceId: localSelectedVoiceId }); // Use local state for voice ID
        } else {
            console.warn("SettingsPage: Cannot play letter, audio context potentially not running.");
            // Optionally show a message to click "Activate Audio"
        }
        // Use a timer to clear the active state for visual feedback
        const timerId = setTimeout(() => setActiveButton(null), 300);
        // Note: No cleanup for this short timer needed in this context
    }, [localSelectedVoiceId, isGeneralSectionDisabled]); // Depend on selected voice and disabled state
    // --- MODIFICATION END ---


    // --- REFACTORED: Handle saving specific App Settings ---
    // Accepts an object of settings to save and a section identifier for status messages
    const handleSaveAppSettings = useCallback(async (settingsToSave, sectionIdentifier) => {
        if (settingKey) {
            setAppSaveStatus({ message: 'Finish setting key before saving.', type: 'error', section: sectionIdentifier });
            return;
        }

        // --- Validation specific to the settings being saved ---
        let validationError = null;
        if (settingsToSave.hasOwnProperty('keyBindings')) {
            const { visual, audio } = settingsToSave.keyBindings;
            if (!visual || !audio) validationError = 'Both Visual and Audio keys must be set.';
            else if (visual === audio) validationError = 'Visual and Audio keys cannot be the same.';
        }
        if (settingsToSave.hasOwnProperty('selectedVoiceId') && !settingsToSave.selectedVoiceId) {
             // Allow null/empty if no voices are available
             if (availableVoices && Object.keys(availableVoices).length > 0) {
                 validationError = 'Please select an audio voice.';
             }
        }
        if (settingsToSave.hasOwnProperty('timezone') && !settingsToSave.timezone) {
            validationError = 'Please select a time zone.';
        }
        if (settingsToSave.hasOwnProperty('enableExperimentalFeatures') && typeof settingsToSave.enableExperimentalFeatures !== 'boolean') {
            validationError = 'Invalid value for experimental features setting.';
        }

        if (validationError) {
            setAppSaveStatus({ message: validationError, type: 'error', section: sectionIdentifier });
            return;
        }

        // --- Proceed with saving ---
        setIsSavingAppSettings(true);
        setAppSaveStatus({ message: '', type: null, section: null }); // Clear status before saving

        try {
            // Create an array of promises for each setting update
            const updatePromises = Object.entries(settingsToSave).map(([key, value]) =>
                updateSetting(key, value) // Assumes updateSetting handles its own logic and returns a promise/is async
            );

            // Wait for all updates to complete
            await Promise.all(updatePromises);

            // Determine success message based on section
            let successMessage = 'Settings saved!';
            if (sectionIdentifier === 'keyBindings') successMessage = 'Key bindings saved!';
            else if (sectionIdentifier === 'gameplay') successMessage = 'Gameplay & audio settings saved!';
            else if (sectionIdentifier === 'timezone') successMessage = 'Time zone saved!';
            else if (sectionIdentifier === 'advanced') successMessage = 'Advanced settings saved!';

            setAppSaveStatus({ message: successMessage, type: 'success', section: sectionIdentifier });

            // Clear success message after delay
            setTimeout(() => setAppSaveStatus(prev =>
                prev.type === 'success' && prev.section === sectionIdentifier
                ? { message: '', type: null, section: null }
                : prev
            ), 3000);

        } catch (err) {
            console.error(`Error saving app settings for section ${sectionIdentifier}:`, err);
            setAppSaveStatus({
                message: appSettingsError || `Failed to save ${sectionIdentifier} settings. Error: ${err.message}`,
                type: 'error',
                section: sectionIdentifier
            });
        } finally {
            setIsSavingAppSettings(false);
        }
    }, [settingKey, updateSetting, appSettingsError, availableVoices]); // Dependencies: settingKey, updateSetting function, context error, availableVoices

    // --- Handlers for Manual Level Change ---

    const validateLevelInputs = useCallback(() => {
        if (!activeProfile) {
            setLevelValidationError('No active profile selected.');
            return false;
        }
        // Add validation if needed (e.g., ensure NBack is a number)
        setLevelValidationError('');
        return true;
    }, [activeProfile]);

    const handleRequestLevelChange = useCallback(() => {
        setLevelSaveStatus({ message: '', type: null });
        if (validateLevelInputs()) {
            setShowLevelConfirmModal(true);
        }
    }, [validateLevelInputs]);

    const handleConfirmLevelChange = useCallback(async () => {
        if (!validateLevelInputs()) {
            setShowLevelConfirmModal(false);
            return;
        }
        setIsSavingLevel(true);
        setLevelSaveStatus({ message: '', type: null });
        setShowLevelConfirmModal(false);
        const nBackToSave = parseInt(localNBack, 10);
        const difficultyToSave = localDifficultySliderValue / 100.0;

        try {
            console.log(`SettingsPage: Calling updateProfileProgress for ${activeProfile?.id} with N=${nBackToSave}, Diff=${difficultyToSave.toFixed(2)}`);
            // Ensure activeProfile.id is valid before calling
            if (!activeProfile?.id) {
                throw new Error("Active profile ID is missing.");
            }
            const success = await updateProfileProgress(activeProfile.id, {
                currentNBack: nBackToSave,
                currentDifficulty: difficultyToSave
            });

            if (success) {
                setLevelSaveStatus({ message: 'Training level updated!', type: 'success' });
                setTimeout(() => setLevelSaveStatus(prev => prev.type === 'success' ? { message: '', type: null } : prev), 3500);
            } else {
                // Use specific error from context if available, otherwise generic message
                setLevelSaveStatus({ message: profileError || 'Failed to save level. Please try again.', type: 'error' });
            }
        } catch (err) {
            console.error("Error directly calling updateProfileProgress:", err);
            setLevelSaveStatus({ message: `Error updating level: ${err.message}`, type: 'error' });
        } finally {
            setIsSavingLevel(false);
        }
    }, [activeProfile, localNBack, localDifficultySliderValue, updateProfileProgress, validateLevelInputs, profileError]);

    const handleCancelLevelChange = useCallback(() => {
        setShowLevelConfirmModal(false);
        setLevelSaveStatus({ message: '', type: null }); // Clear status on cancel
    }, []);

    // --- Derived Values ---

    // N-Back options
    const nBackOptions = useMemo(() => {
        const options = [];
        // --- MODIFICATION START: Generate options from 2 to 20 ---
        const MIN_LEVEL = 2;
        const MAX_LEVEL = 20;
        for (let i = MIN_LEVEL; i <= MAX_LEVEL; i++) {
            options.push({ value: String(i), label: String(i) });
        }
        // --- MODIFICATION END ---
        return options;
    }, []); // No dependency on activeProfile needed for a fixed range

    // Displayed combined level
    const displayedCombinedLevel = useMemo(() => {
        const nBackNum = parseInt(localNBack, 10) || 0;
        // Ensure slider value is treated as a number between 0 and 99
        const difficulty = Math.max(0, Math.min(99, localDifficultySliderValue)) / 100.0;
        return (nBackNum + difficulty).toFixed(2);
    }, [localNBack, localDifficultySliderValue]);

     // Format available voices for the DropdownInput component
     const voiceOptions = useMemo(() => {
        if (!availableVoices || Object.keys(availableVoices).length === 0) return [];
        return Object.entries(availableVoices).map(([id, voice]) => ({
            value: id, // e.g., "google-chirp3-hd-en-us-charon"
            label: voice.name || id // Use name if available, otherwise fallback to ID
        }));
    }, [availableVoices]);

    // Format current local time in selected timezone
    const currentLocalTime = useMemo(() => {
        try {
            return new Date().toLocaleTimeString([], {
                timeZone: localTimezone,
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true
            });
        } catch (e) {
            console.error("Error formatting time for timezone:", localTimezone, e);
            // Fallback to browser default if selected timezone is invalid
            try {
                 return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
            } catch { return "Invalid Time"; }
        }
    }, [localTimezone]);

    // Format current local date in selected timezone
    const currentLocalDate = useMemo(() => {
        try {
            return new Date().toLocaleDateString([], {
                timeZone: localTimezone,
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                weekday: 'long'
            });
        } catch (e) {
            console.error("Error formatting date for timezone:", localTimezone, e);
             // Fallback to browser default if selected timezone is invalid
            try {
                return new Date().toLocaleDateString([], { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' });
            } catch { return "Invalid Date"; }
        }
    }, [localTimezone]);

    // --- Render Logic ---

    const isLoading = isLoadingAppSettings || isLoadingProfile;

    if (isLoading) {
        return <div className="p-6 text-center text-gray-500">Loading settings and profile...</div>;
    }

    // Use the already defined isGeneralSectionDisabled for disabling audio test buttons too
    // const isAnySaving = isSavingAppSettings || isSavingLevel; // Already defined
    // const isKeySettingInProgress = !!settingKey; // Already defined
    // const isGeneralSectionDisabled = isAnySaving || isKeySettingInProgress; // Already defined
    const isLevelSectionDisabled = isAnySaving || isKeySettingInProgress || !activeProfile; // Level section also disabled if no profile

    // --- MODIFICATION START: Define sample letters for test ---
    const sampleLetters = ['B', 'D', 'K', 'P', 'T', 'X'];
    // --- MODIFICATION END ---


    return (
        <div className="p-4 sm:p-6 bg-gray-100 min-h-screen">
            <div className="max-w-2xl mx-auto relative">
                {/* Page Header */}
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 flex items-center">
                        <FontAwesomeIcon icon={faSlidersH} className="mr-3 text-indigo-600 h-6 w-6" /> {/* Added icon */}
                        Profile Settings
                    </h1>
                    <button
                        onClick={() => navigate('/dashboard')}
                        className="text-sm text-indigo-600 hover:text-indigo-800 hover:underline focus:outline-none focus:ring-1 focus:ring-indigo-500 rounded px-1"
                    >
                        ← Back to Dashboard
                    </button>
                </div>

                {/* Display Global Context Errors */}
                {appSettingsError && <p className="mb-4 text-sm text-red-600 bg-red-50 p-3 rounded border border-red-200">{appSettingsError}</p>}
                {profileError && <p className="mb-4 text-sm text-red-600 bg-red-50 p-3 rounded border border-red-200">{profileError}</p>}

                {/* --- Game Key Bindings Card --- */}
                <SectionCard title="Game Key Bindings" icon={faKeyboard}>
                    <p className="text-sm text-gray-600 mb-4">
                        Customize the keys used for matching during the game. Click a button, then press the desired key (Escape and Tab cannot be bound).
                    </p>
                    <div className="space-y-3">
                        <KeyBindingInput
                            label="Position Match Key"
                            currentKey={localKeyBindings.visual}
                            isSetting={settingKey === 'visual'}
                            onStartSetting={() => handleStartSettingKey('visual')}
                            disabled={isGeneralSectionDisabled} // Disable input if saving/setting
                        />
                        <KeyBindingInput
                            label="Audio Match Key"
                            currentKey={localKeyBindings.audio}
                            isSetting={settingKey === 'audio'}
                            onStartSetting={() => handleStartSettingKey('audio')}
                            disabled={isGeneralSectionDisabled} // Disable input if saving/setting
                        />
                    </div>
                    {/* --- Save Button Area for Key Bindings --- */}
                    <div className="mt-5 pt-4 border-t border-gray-200 flex items-center justify-end space-x-3">
                        {/* Show status specific to this section */}
                        {appSaveStatus.section === 'keyBindings' && appSaveStatus.message && (
                            <span className={`text-sm font-medium ${appSaveStatus.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                                {appSaveStatus.message}
                            </span>
                        )}
                        <button
                            // Call refactored save function with ONLY key bindings
                            onClick={() => handleSaveAppSettings({ keyBindings: localKeyBindings }, 'keyBindings')}
                            disabled={isGeneralSectionDisabled} // Disable if saving OR setting a key
                            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 transition-colors flex items-center justify-center min-w-[150px] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" // Adjusted min-width
                        >
                            {isSavingAppSettings && appSaveStatus.section === 'keyBindings' ? <><LoadingSpinner /> Saving...</> : 'Save Key Bindings'}
                        </button>
                    </div>
                </SectionCard>

                {/* --- Gameplay & Audio Card --- */}
                <SectionCard title="Gameplay & Audio" icon={faCog}>
                     <div className="space-y-3 divide-y divide-gray-200">
                        {/* Instant Feedback */}
                        <ToggleSwitch
                            label="Instant Feedback"
                            description="Show visual/audio cues immediately after a match response or miss."
                            checked={localInstantFeedback}
                            onChange={setLocalInstantFeedback}
                            disabled={isGeneralSectionDisabled}
                        />
                        {/* Audio Voice Selection */}
                        <DropdownInput
                            label="Audio Voice"
                            id="audioVoice"
                            value={localSelectedVoiceId}
                            onChange={(e) => setLocalSelectedVoiceId(e.target.value)}
                            options={voiceOptions}
                            disabled={isGeneralSectionDisabled || voiceOptions.length === 0}
                            description={voiceOptions.length === 0 ? "No voices available" : "Select the voice for letter sounds."}
                            className="pt-3"
                         />
                         {/* --- MODIFICATION START: Inline Audio Test --- */}
                        <div className="pt-3">
                             <div className="flex items-center justify-between mb-3">
                                <span className="flex-grow flex flex-col mr-4">
                                    <span className="text-sm font-medium text-gray-700">Audio Output Test</span>
                                    <span className="text-xs text-gray-500 mt-0.5">Check letter sounds for the selected voice.</span>
                                </span>
                                {!audioReady && (
                                    <button
                                        className="px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600 text-sm disabled:opacity-50"
                                        onClick={handleResumeAudio}
                                        disabled={isGeneralSectionDisabled}
                                    >
                                        Activate Audio
                                    </button>
                                )}
                             </div>

                             {!audioReady && (
                                 <p className="text-xs text-yellow-700 mb-2">Click "Activate Audio" first if sounds don't play.</p>
                             )}

                             {/* Grid for testing letters */}
                             <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                               {sampleLetters.map(letter => (
                                 <button
                                   key={letter}
                                   className={`px-4 py-2 rounded text-sm transition-colors ${
                                     activeButton === `letter-${letter}`
                                       ? 'bg-blue-300 text-blue-900' // Highlight when active
                                       : 'bg-blue-100 hover:bg-blue-200 text-blue-800'
                                   } ${isGeneralSectionDisabled || !audioReady ? 'opacity-50 cursor-not-allowed' : ''}`} // Disable if generally disabled OR audio not ready
                                   onClick={() => handleTestLetter(letter)}
                                   disabled={isGeneralSectionDisabled || !audioReady} // Disable button based on states
                                 >
                                   {letter}
                                 </button>
                               ))}
                             </div>
                        </div>
                         {/* --- MODIFICATION END --- */}
                    </div>
                    {/* --- Save Button Area for Gameplay & Audio --- */}
                    <div className="mt-5 pt-4 border-t border-gray-200 flex items-center justify-end space-x-3">
                        {/* Show status specific to this section */}
                        {appSaveStatus.section === 'gameplay' && appSaveStatus.message && (
                            <span className={`text-sm font-medium ${appSaveStatus.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                                {appSaveStatus.message}
                            </span>
                        )}
                        <button
                             // Call refactored save function with ONLY feedback and voice
                            onClick={() => handleSaveAppSettings({
                                instantFeedback: localInstantFeedback,
                                selectedVoiceId: localSelectedVoiceId
                            }, 'gameplay')}
                            disabled={isGeneralSectionDisabled}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 transition-colors flex items-center justify-center min-w-[200px] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" // Adjusted min-width
                        >
                            {isSavingAppSettings && appSaveStatus.section === 'gameplay' ? <><LoadingSpinner /> Saving...</> : 'Save Gameplay & Audio'}
                        </button>
                    </div>
                </SectionCard>

                {/* --- Time Zone Card --- */}
                <SectionCard title="Time Zone" icon={faClock}>
                    <p className="text-sm text-gray-600 mb-4">
                        Select your time zone for accurate session timing and progress tracking.
                    </p>
                    <div className="space-y-3">
                        <DropdownInput
                            label="Time Zone"
                            id="timezone"
                            value={localTimezone}
                            onChange={(e) => setLocalTimezone(e.target.value)}
                            options={TIMEZONE_OPTIONS}
                            disabled={isGeneralSectionDisabled}
                            className="mb-2"
                        />
                        <div className="bg-gray-50 p-3 rounded-md border border-gray-200">
                            <div className="text-sm font-medium text-gray-600">Current Local Time:</div>
                            <div className="mt-1 text-base font-semibold text-gray-700">{currentLocalTime}</div>
                            <div className="mt-1 text-sm text-gray-500">{currentLocalDate}</div>
                        </div>
                    </div>
                    {/* --- Save Button Area for Time Zone --- */}
                    <div className="mt-5 pt-4 border-t border-gray-200 flex items-center justify-end space-x-3">
                        {/* Show status specific to this section */}
                        {appSaveStatus.section === 'timezone' && appSaveStatus.message && (
                            <span className={`text-sm font-medium ${appSaveStatus.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                                {appSaveStatus.message}
                            </span>
                        )}
                        <button
                            // Call refactored save function with ONLY timezone
                            onClick={() => handleSaveAppSettings({ timezone: localTimezone }, 'timezone')}
                            disabled={isGeneralSectionDisabled}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 transition-colors flex items-center justify-center min-w-[150px] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" // Adjusted min-width
                        >
                            {isSavingAppSettings && appSaveStatus.section === 'timezone' ? <><LoadingSpinner /> Saving...</> : 'Save Time Zone'}
                        </button>
                    </div>
                </SectionCard>

                {/* --- Advanced Settings Card (including Experimental Features) --- */}
                <SectionCard title="Advanced Settings" icon={faFlask}> {/* Using faFlask icon for Experimental */}
                    <div className="space-y-3 divide-y divide-gray-200">
                        <ToggleSwitch
                            label="Enable Experimental Features"
                            description="Access experimental and beta features. These may be unstable or change frequently."
                            checked={localEnableExperimentalFeatures}
                            onChange={setLocalEnableExperimentalFeatures}
                            disabled={isGeneralSectionDisabled}
                        />
                    </div>
                    {/* Save Button Area for Advanced Settings */}
                    <div className="mt-5 pt-4 border-t border-gray-200 flex items-center justify-end space-x-3">
                        {appSaveStatus.section === 'advanced' && appSaveStatus.message && (
                            <span className={`text-sm font-medium ${appSaveStatus.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                                {appSaveStatus.message}
                            </span>
                        )}
                        <button
                            onClick={() => handleSaveAppSettings({ enableExperimentalFeatures: localEnableExperimentalFeatures }, 'advanced')}
                            disabled={isGeneralSectionDisabled}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 transition-colors flex items-center justify-center min-w-[180px] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                            {isSavingAppSettings && appSaveStatus.section === 'advanced' ? <><LoadingSpinner /> Saving...</> : 'Save Advanced Settings'}
                        </button>
                    </div>
                </SectionCard>
                {/* --- Training Level Card --- */}
                <SectionCard title="Training Level" icon={faBrain}>
                    {!activeProfile ? (
                        <p className="text-sm text-gray-500 italic">Select an active profile to adjust training level.</p>
                    ) : (
                        <>
                            <p className="text-sm text-gray-600 mb-4">
                                Manually set the starting N-Back level and difficulty component for your next standard training session. The system normally adjusts this automatically.
                            </p>
                            <div className="space-y-3 divide-y divide-gray-200">
                                {/* N-Back Dropdown */}
                                <DropdownInput
                                    label="N-Back Level"
                                    id="nBackLevel"
                                    value={localNBack}
                                    onChange={(e) => setLocalNBack(e.target.value)}
                                    options={nBackOptions}
                                    disabled={isLevelSectionDisabled}
                                    className="pt-3"
                                />
                                {/* Difficulty Slider */}
                                <SliderInput
                                    label="Level Difficulty"
                                    id="difficultyLevel"
                                    value={String(localDifficultySliderValue)}
                                    onChange={(e) => setLocalDifficultySliderValue(parseInt(e.target.value, 10))}
                                    min="0"
                                    max="99" // Slider goes 0-99
                                    step="1"
                                    disabled={isLevelSectionDisabled}
                                    displayValue={displayedCombinedLevel} // Displays N + 0.XX
                                />
                            </div>
                            {levelValidationError && (
                                <p className="mt-3 text-sm text-red-600">{levelValidationError}</p>
                            )}
                             {/* --- Save Button Area for Level Settings --- */}
                            <div className="mt-5 pt-4 border-t border-gray-200 flex items-center justify-end space-x-3">
                                {levelSaveStatus.message && (
                                    <span className={`text-sm font-medium ${levelSaveStatus.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                                        {levelSaveStatus.message}
                                    </span>
                                 )}
                                <button
                                    onClick={handleRequestLevelChange}
                                    disabled={isLevelSectionDisabled || !!levelValidationError}
                                    className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:opacity-50 transition-colors flex items-center justify-center min-w-[120px] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                                >
                                    {isSavingLevel ? <><FontAwesomeIcon icon={faSpinner} className="animate-spin -ml-1 mr-2 h-4 w-4" /> Saving...</> : 'Save Level'}
                                </button>
                            </div>
                        </>
                    )}
                </SectionCard>

                {/* --- MODIFICATION START: Remove Audio Test Modal Rendering --- */}
                {/* {showAudioTest && ( ... )} */}
                {/* --- MODIFICATION END --- */}


                {/* Level Change Confirmation Modal Rendering */}
                <ConfirmationModal
                    isOpen={showLevelConfirmModal}
                    title="Confirm Manual Level Change"
                    // Pass message content as children
                    onConfirm={handleConfirmLevelChange}
                    onClose={handleCancelLevelChange}
                    confirmText="Change Level"
                    cancelText="Cancel"
                    isDestructive={false} // Not a destructive action
                 >
                    <p className="text-sm text-gray-600">Recommendation: For optimal results and accurate progress tracking, allow the system to automatically adjust your N-Back level based on performance.</p>
                    <p className="text-sm text-gray-600 mt-2">Manually setting the level can be useful for experimentation or specific goals, but may disrupt the intended adaptive training flow.</p>
                    <p className="text-sm text-gray-800 font-medium mt-3">Continue with manual change?</p>
                 </ConfirmationModal>

            </div> {/* End max-w-2xl container */}
        </div> // End page wrapper
    );
};

export default SettingsPage;